import { SourceSystemResourceFilter } from './components/SourceSystemResourceFilter';
// External Libraries
import { Filter, Shield, Users } from "lucide-react";
import React, { useMemo } from "react";

// Internal Modules
import { DonutPieChart } from "../../components/ChartsComponent";
import { DataTable, type Column } from "../../components/DataTable";
import { MultiSelectDropdown } from "../../components/MultiSelectDropdown";
import { RowsPerCardSelect } from "../../components/RowsPerCardSelect";
import { SearchInput } from "../../components/SearchInput";
import { SkeletonCard } from "../../components/Skeleton";
import { TableToolbar } from "../../components/TableToolbar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tip } from "../../components/ui/tooltip";

// Relative Imports
import { CyberarkPlatformsBreakdown } from "./components/CyberarkPlatformsBreakdown";
import { PagChartDrilldownDrawer } from "./components/PagChartDrilldownDrawer";
import { SimpleBreakdownPagination } from "./components/SimpleBreakdownPagination";
import { ChartEmptyState, ChartSummary, SpikeChart } from "./components/SpikeChart";
import type { ChartDatum } from "./constants/pagInsightsConstants";
import { resourceOptions, sourceSystemOptions } from "./helpers/pagHelpers";
import {
  buildAccountColumns,
  buildDrilldownColumns,
} from "./helpers/pagInsightsColumns";
import { useInsightsData } from "./hooks/useInsightsData";
import type {
  PagInventoryBreakdownGroup,
  PagPrivilegedAccountBreakdownGroup,
} from "./services/pagApi";

/* Re-export the badges so existing imports from other files keep working. */
export { ReviewBadge, StatusPill, TextCell } from "./components/StatusBadges";

/* ── Pure Helpers ───────────────────────────────────────────────────── */

function getSourceSystemBadge(type?: string | null, name?: string | null) {
  const raw = (type || name || "").trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();

  if (lower.includes("active directory") || lower === "ad") {
    return {
      label: "Active Directory",
      title: raw || "Active Directory",
      className: "border-blue-500/30 bg-blue-500/10 text-blue-400",
    };
  }
  if (lower.includes("linux")) {
    return {
      label: "Linux",
      title: raw || "Linux",
      className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    };
  }
  if (lower.includes("aws")) {
    return {
      label: "AWS",
      title: raw || "AWS",
      className: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    };
  }
  return {
    label: raw,
    title: raw,
    className: "border-border/60 bg-muted/50 text-muted-foreground",
  };
}

/** Determines if a group row represents unassigned/no-membership accounts. */
function isNoGroupMembership(
  groupId?: string | number | null,
  groupName?: string | null,
  description?: string | null,
): boolean {
  if (!groupId && groupId !== 0) return true;
  const name = groupName?.toLowerCase() ?? "";
  const desc = description?.toLowerCase() ?? "";
  return name.includes("not in any group") || desc.includes("no group membership");
}

/* ── Reusable Cell Component ────────────────────────────────────────── */

interface DrilldownCountButtonProps {
  count: number;
  isNoGroup: boolean;
  groupName: string;
  onClick: () => void;
  colorClass?: string;
  titlePrefix?: string;
}

function DrilldownCountButton({
  count,
  isNoGroup,
  groupName,
  onClick,
  colorClass = "text-indigo-400 hover:text-indigo-300",
  titlePrefix = "",
}: DrilldownCountButtonProps) {
  if (count > 0 && !isNoGroup) {
    const noun = count === 1 ? "account" : "accounts";
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClick}
        className={`h-auto p-0 font-semibold tabular-nums ${colorClass} hover:underline cursor-pointer transition-colors bg-transparent hover:bg-transparent`}
        title={`View ${count} ${titlePrefix}${noun} in ${groupName}`.trim()}
      >
        {count.toLocaleString()}
      </Button>
    );
  }

  if (isNoGroup) {
    return (
      <Tip label="No drilldown available: accounts hold no group membership">
        <span className="tabular-nums font-semibold text-foreground cursor-default">
          {count > 0 ? count.toLocaleString() : "0"}
        </span>
      </Tip>
    );
  }

  return (
    <span className="tabular-nums font-semibold text-foreground">
      {count > 0 ? count.toLocaleString() : "0"}
    </span>
  );
}

/* ── Column Builders ────────────────────────────────────────────────── */

function buildInventoryColumns(
  sourceSystemName: string | undefined,
  openDrilldown: ReturnType<typeof useInsightsData>["openDrilldown"],
): Column<PagInventoryBreakdownGroup>[] {
  return [
    {
      key: "groupName",
      header: "Group",
      width: "28%",
      sortable: true,
      render: (group) => (
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={`size-1.5 shrink-0 rounded-full ${
              group.privileged ? "bg-orange-400" : "bg-indigo-400"
            }`}
          />
          <span className="truncate font-medium text-foreground" title={group.groupName}>
            {group.groupName}
          </span>
        </div>
      ),
    },
    {
      key: "source",
      header: "Source",
      width: "18%",
      sortable: true,
      render: (group) => {
        const badge = getSourceSystemBadge(
          group.sourceSystemType,
          group.sourceSystemName ?? sourceSystemName,
        );
        if (!badge) {
          return <span className="text-xs text-muted-foreground">-</span>;
        }
        return (
          <span
            title={badge.title}
            className={`inline-flex max-w-full items-center rounded px-2 py-0.5 text-xs font-medium border ${badge.className}`}
          >
            <span className="truncate">{badge.label}</span>
          </span>
        );
      },
    },
    {
      key: "description",
      header: "Description",
      width: "30%",
      sortable: true,
      render: (group) => (
        <span className="line-clamp-2 text-xs text-muted-foreground" title={group.description ?? undefined}>
          {group.description ?? "-"}
        </span>
      ),
    },
    {
      key: "accountCount",
      header: "Accounts",
      width: "12%",
      align: "right",
      headerAlign: "right",
      sortable: true,
      sortIconAfterText: true,
      render: (group) => {
        const isNoGroup = isNoGroupMembership(group.groupId, group.groupName, group.description);
        return (
          <DrilldownCountButton
            count={group.accountCount}
            isNoGroup={isNoGroup}
            groupName={group.groupName}
            onClick={() =>
              openDrilldown("inventory-group", "Account Inventory Breakdown", {
                name: group.groupName,
                value: group.accountCount,
                sourceSystemIds:
                  group.sourceSystemId != null && group.sourceSystemId > 0
                    ? [group.sourceSystemId]
                    : undefined,
              })
            }
          />
        );
      },
    },
    {
      key: "percentage",
      header: "% of Total",
      width: "12%",
      align: "right",
      headerAlign: "right",
      sortable: true,
      sortIconAfterText: true,
      render: (group) => (
        <span className="tabular-nums font-semibold text-muted-foreground">
          {group.percentage}%
        </span>
      ),
    },
  ];
}

function buildPrivilegedColumns(
  sourceSystemName: string | undefined,
  openDrilldown: ReturnType<typeof useInsightsData>["openDrilldown"],
): Column<PagPrivilegedAccountBreakdownGroup>[] {
  return [
    {
      key: "groupName",
      header: "Group",
      width: "26%",
      sortable: true,
      render: (row) => (
        <div className="flex min-w-0 items-center gap-2">
          <div className="size-1.5 shrink-0 rounded-full bg-indigo-400" />
          <span className="truncate font-medium text-foreground" title={row.groupName}>
            {row.groupName}
          </span>
        </div>
      ),
    },
    {
      key: "source",
      header: "Source",
      width: "18%",
      sortable: true,
      render: (row) => {
        const badge = getSourceSystemBadge(
          row.sourceSystemType,
          row.sourceSystemName ?? sourceSystemName,
        );
        if (!badge) {
          return <span className="text-xs text-muted-foreground">-</span>;
        }
        return (
          <span
            title={badge.title}
            className={`inline-flex max-w-full items-center rounded px-2 py-0.5 text-xs font-medium border ${badge.className}`}
          >
            <span className="truncate">{badge.label}</span>
          </span>
        );
      },
    },
    {
      key: "description",
      header: "Description",
      width: "26%",
      sortable: true,
      render: (row) => (
        <span className="line-clamp-2 text-xs text-muted-foreground" title={row.description ?? undefined}>
          {row.description ?? "-"}
        </span>
      ),
    },
    {
      key: "privilegedAccountCount",
      header: "Privileged",
      width: "10%",
      align: "right",
      headerAlign: "right",
      sortable: true,
      sortIconAfterText: true,
      render: (row) => {
        const isNoGroup = isNoGroupMembership(row.groupId, row.groupName, row.description);
        return (
          <DrilldownCountButton
            count={row.privilegedAccountCount}
            isNoGroup={isNoGroup}
            groupName={row.groupName}
            titlePrefix="privileged "
            onClick={() =>
              openDrilldown("group", "Privileged Account Breakdown", {
                name: row.groupName,
                value: row.privilegedAccountCount,
                sourceSystemIds:
                  row.sourceSystemId != null && row.sourceSystemId > 0
                    ? [row.sourceSystemId]
                    : undefined,
              })
            }
          />
        );
      },
    },
    {
      key: "pamCoveredCount",
      header: "In PAM",
      width: "10%",
      align: "right",
      headerAlign: "right",
      sortable: true,
      sortIconAfterText: true,
      render: (row) => {
        const isNoGroup = isNoGroupMembership(row.groupId, row.groupName, row.description);
        return (
          <DrilldownCountButton
            count={row.pamCoveredCount}
            isNoGroup={isNoGroup}
            groupName={row.groupName}
            titlePrefix="In PAM "
            colorClass="text-green-400 hover:text-green-300"
            onClick={() =>
              openDrilldown("group-pam", "Privileged Account Breakdown · In PAM", {
                name: `${row.groupName} (In PAM)`,
                key: row.groupName,
                value: row.pamCoveredCount,
                sourceSystemIds:
                  row.sourceSystemId != null && row.sourceSystemId > 0
                    ? [row.sourceSystemId]
                    : undefined,
              })
            }
          />
        );
      },
    },
    {
      key: "pamCoveragePercentage",
      header: "Coverage",
      width: "10%",
      align: "right",
      headerAlign: "right",
      sortable: true,
      sortIconAfterText: true,
      render: (row) => (
        <span className="tabular-nums font-semibold text-muted-foreground">
          {row.pamCoveragePercentage}%
        </span>
      ),
    },
  ];
}

/* ── Subcomponents ──────────────────────────────────────────────────── */

interface InsightsTopFiltersProps {
  data: ReturnType<typeof useInsightsData>;
}

function InsightsTopFilters({ data: d }: InsightsTopFiltersProps) {
  const handleFilterClick = () => {
    const idsToApply = d.stagedSourceSystemIds.length
      ? d.stagedSourceSystemIds
      : resourceOptions(d.sourceSystemFilters, d.sourceSystems)
        .map((opt) => Number(opt.value))
        .filter((id) => Number.isFinite(id) && id > 0);

    if (idsToApply.length) {
      d.setAppliedSourceSystemIds(idsToApply);
    }
    d.setPage(0);
    d.setScopeLoading(true);
    /* Same scope as before still re-reads, so Filter always refreshes. */
    d.setReloadToken((token) => token + 1);
  };

  const handleClearClick = () => {
    d.changeSourceSystems([]);
    d.setResources([]);
    d.setAppliedSourceSystemIds([]);
    d.setPage(0);
    d.setScopeLoading(true);
    d.setReloadToken((token) => token + 1);
  };

  const isFilterDisabled =
    d.insightsLoading ||
    d.scopeLoading ||
    d.sourceSystems.length === 0 ||
    d.resources.length === 0;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-4">

      <SourceSystemResourceFilter
        sourceSystemFilters={d.sourceSystemFilters}
        sourceSystems={d.sourceSystems}
        resources={d.resources}
        onSourceSystemsChange={d.changeSourceSystems}
        onResourcesChange={d.setResources}
      />
      <div className="flex items-center gap-2">
        <Button
          onClick={handleFilterClick}
          disabled={isFilterDisabled}
          variant="indigo"
          className="justify-center min-w-[92px] gap-1.5 px-5 font-semibold disabled:opacity-60"
        >
          <Filter className="size-3.5" />
          Filter
        </Button>
        <Button
          onClick={handleClearClick}
          disabled={d.insightsLoading || d.scopeLoading}
          variant="outline"
          className="justify-center min-w-[92px] px-5 hover:border-indigo-400/60 hover:bg-muted/40 disabled:opacity-60"
        >
          Clear
        </Button>
      </div>
    </div>
  );
}

function InsightsEmptyPrompt() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border bg-card/50 py-16 px-6 text-center shadow-sm">
      <div className="flex size-12 items-center justify-center rounded-full bg-indigo-500/10 text-indigo-500 dark:text-indigo-400">
        <Filter className="size-6" />
      </div>
      <h3 className="text-base font-semibold text-foreground">
        Select a source system and resource to view data.
      </h3>
      <p className="max-w-md text-sm text-muted-foreground">
        Please choose a source system and resource, then click Filter to view account inventory and breakdown details.
      </p>
    </div>
  );
}

interface InsightsBreakdownsSectionProps {
  data: ReturnType<typeof useInsightsData>;
  inventoryColumns: Column<PagInventoryBreakdownGroup>[];
  privilegedColumns: Column<PagPrivilegedAccountBreakdownGroup>[];
}

function InsightsBreakdownsSection({
  data: d,
  inventoryColumns,
  privilegedColumns,
}: InsightsBreakdownsSectionProps) {
  if (d.insightsLoading) {
    return (
      <div className="flex flex-col gap-5">
        <SkeletonCard lines={6} />
        <SkeletonCard lines={6} />
      </div>
    );
  }

  const inventoryTotalPages =
    d.breakdown?.groups?.totalPages ??
    Math.ceil((d.breakdown?.groups?.totalElements ?? 0) / d.inventoryPageSize);

  const privilegedTotalPages =
    d.privilegedAccountBreakdown?.groups?.totalPages ??
    Math.ceil(
      (d.privilegedAccountBreakdown?.groups?.totalElements ?? 0) / d.privilegedPageSize,
    );

  const handleOpenAllPrivileged = () => {
    const total = d.privilegedAccountBreakdown?.totalPrivilegedAccounts ?? 0;
    if (total > 0) {
      d.openDrilldown("all", "Privileged Account Breakdown", {
        name: "All Privileged Accounts",
        value: total,
      });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Account Inventory */}
      <div className="flex flex-col gap-5 rounded-2xl border border-border/80 bg-card p-5 lg:flex-row shadow-sm">
        <div className="flex w-full shrink-0 flex-col rounded-xl border border-amber-400/80 bg-amber-50/20 dark:bg-amber-500/[0.04] p-5 lg:w-[200px]">
          <div className="mb-3 flex size-9 items-center justify-center rounded-lg bg-yellow-500/10">
            <Users className="size-5 text-yellow-500" />
          </div>
          <div className="text-4xl font-bold text-foreground">
            {d.inventoryTotal.toLocaleString()}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">Total Accounts</div>
          <div className="mt-1 text-[11px] text-muted-foreground">All accounts scanned</div>
          <div className="mt-auto pt-3 text-[11px] text-yellow-500">
            {d.privilegedTotal} privileged - {d.privilegedPct}%
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex size-7 items-center justify-center rounded-md bg-indigo-500/10">
                <Users className="size-4 text-indigo-400" />
              </div>
              <h3 className="font-semibold text-foreground">Account Inventory Breakdown</h3>
              <Badge variant="secondary" className="border-indigo-500/20 bg-indigo-500/10 text-indigo-400">
                Total: {d.inventoryTotal.toLocaleString()} Accounts
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <SearchInput
                size="sm"
                className="w-48"
                value={d.inventorySearch}
                onChange={d.setInventorySearch}
                onSubmit={(term) => void d.searchInventory(term)}
                disabled={d.inventorySearching}
                placeholder="Search"
                ariaLabel="Search account inventory groups"
              />
              <RowsPerCardSelect
                value={d.inventoryPageSize}
                onChange={d.changeInventoryPageSize}
                label="Rows per page"
              />
            </div>
          </div>
          <div className="mt-1 flex min-h-0 flex-1 flex-col">
            <DataTable
              borderless
              columns={inventoryColumns}
              data={d.inventoryGroups}
              keyField="groupId"
              loading={d.inventoryLoading}
              maxHeight="320px"
              minTableWidth="720px"
              sortKey={d.inventorySortKey}
              sortDir={d.inventorySortDir}
              onSort={(key) => d.changeInventorySort(key as any)}
              emptyMessage="No Data Available"
            />
          </div>
          <SimpleBreakdownPagination
            currentPage={d.inventoryPage}
            totalPages={inventoryTotalPages}
            totalElements={d.breakdown?.groups?.totalElements ?? 0}
            pageSize={d.inventoryPageSize}
            loading={d.inventoryLoading}
            onPageChange={d.changeInventoryPage}
            itemLabel="groups"
          />
        </div>
      </div>

      {/* Privileged Account */}
      <div className="flex flex-col gap-5 rounded-2xl border border-border/80 bg-card p-5 lg:flex-row shadow-sm">
        <div className="flex w-full shrink-0 flex-col rounded-xl border border-indigo-400/80 bg-indigo-50/20 dark:bg-indigo-500/[0.04] p-5 lg:w-[200px]">
          <div className="mb-3 flex size-9 items-center justify-center rounded-lg bg-indigo-500/10">
            <Shield className="size-5 text-indigo-400" />
          </div>
          <div
            onClick={handleOpenAllPrivileged}
            className="text-4xl font-bold text-foreground cursor-pointer hover:text-indigo-400 transition-colors"
            title="View all privileged accounts"
          >
            {d.privilegedAccountBreakdown?.totalPrivilegedAccounts}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">Privileged Accounts</div>
          <div className="mt-1 text-[11px] text-muted-foreground">{d.privilegedPct}% of total</div>
          <div className="mt-auto pt-3 text-[11px] text-indigo-400">
            PAM Coverage - {d.privilegedAccountBreakdown?.pamCoveragePercentage}%
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-md bg-green-500/10">
              <Shield className="size-4 text-green-400" />
            </div>
            <h3 className="font-semibold text-foreground">Privileged Account Breakdown</h3>
            <Badge
              variant="secondary"
              onClick={handleOpenAllPrivileged}
              className="border-indigo-500/20 bg-indigo-500/10 text-indigo-400 cursor-pointer hover:bg-indigo-500/20 transition-colors"
              title="View all privileged accounts"
            >
              Total Privileged Accounts: {d.privilegedAccountBreakdown?.totalPrivilegedAccounts}
            </Badge>
            <Badge variant="secondary" className="border-green-500/20 bg-green-500/10 text-green-400">
              PAM Coverage: {d.privilegedAccountBreakdown?.pamCoveredAccounts} of{" "}
              {d.privilegedAccountBreakdown?.totalPrivilegedAccounts} (
              {d.privilegedAccountBreakdown?.pamCoveragePercentage}%)
            </Badge>
            <div className="ml-auto flex flex-wrap items-center gap-3">
              <SearchInput
                size="sm"
                className="w-48"
                value={d.privilegedSearch}
                onChange={d.setPrivilegedSearch}
                onSubmit={(term) => void d.searchPrivileged(term)}
                disabled={d.privilegedSearching}
                placeholder="Search"
                ariaLabel="Search privileged account groups"
              />
              <RowsPerCardSelect
                value={d.privilegedPageSize}
                onChange={d.changePrivilegedPageSize}
                label="Rows per page"
              />
            </div>
          </div>
          <div className="mt-1 flex min-h-0 flex-1 flex-col">
            <DataTable
              borderless
              columns={privilegedColumns}
              data={d.privilegedGroups}
              keyField="groupId"
              loading={d.privilegedLoading}
              maxHeight="320px"
              minTableWidth="760px"
              sortKey={d.privilegedSortKey}
              sortDir={d.privilegedSortDir}
              onSort={(key) => d.changePrivilegedSort(key as any)}
              emptyMessage="No Data Available"
            />
          </div>
          <SimpleBreakdownPagination
            currentPage={d.privilegedPage}
            totalPages={privilegedTotalPages}
            totalElements={d.privilegedAccountBreakdown?.groups?.totalElements ?? 0}
            pageSize={d.privilegedPageSize}
            loading={d.privilegedLoading}
            onPageChange={d.changePrivilegedPage}
            itemLabel="groups"
          />
        </div>
      </div>

      {/* CyberArk Platforms Breakdown */}
      <CyberarkPlatformsBreakdown
        sourceSystemIds={d.appliedSourceSystemIds}
        reloadToken={d.reloadToken}
      />
    </div>
  );
}

interface InsightsChartsSectionProps {
  data: ReturnType<typeof useInsightsData>;
}

function InsightsChartsSection({ data: d }: InsightsChartsSectionProps) {
  if (d.insightsLoading) {
    return (
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
        <SkeletonCard lines={5} />
        <SkeletonCard lines={5} />
        <SkeletonCard lines={5} />
      </div>
    );
  }

  const hasRiskData = d.riskDistribution.some((slice: ChartDatum) => slice.value > 0);
  const hasAccountsByGroup = d.accountsByGroup.length > 0;

  return (
    <div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
      {/* Risk Chart */}
      <div className="flex flex-col min-w-0 rounded-xl border border-border bg-card p-5">
        <h3 className="mb-4 font-semibold text-foreground">Privileged Account By Risk Level</h3>
        {hasRiskData ? (
          <SpikeChart
            data={d.riskDistribution}
            prefix="pag-risk"
            tickVariant="badge"
            onSelect={(datum) =>
              d.openDrilldown("risk", "Privileged Account By Risk Level", datum)
            }
          />
        ) : (
          <ChartEmptyState />
        )}
        <ChartSummary
          data={d.riskDistribution}
          highestLabel="Highest Risk Level"
          lowestLabel="Lowest Risk Level"
        />
      </div>

      {/* PAM Coverage Donut */}
      <div className="flex flex-col min-w-0 rounded-xl border border-border bg-card p-5">
        <h3 className="mb-4 font-semibold text-foreground">PAM Coverage</h3>
        <DonutPieChart
          id="pag-pam-coverage-donut"
          name="PAM Coverage"
          data={d.pamDistribution}
          height={250}
          sort="none"
          maxSlices={d.pamDistribution.length}
          centerLabel="Total"
          legendPosition="bottom"
          legendToggle
          showDataTable={false}
          showSliceLabels
          labelStyle="leader"
          emptyMessage="No data is available"
          zeroValuesMessage="No data is available"
          ariaLabel="Donut chart: PAM coverage of privileged accounts"
          className="[&_ul]:mx-auto [&_*:focus]:outline-none [&_*:focus-visible]:outline-none"
          onSliceClick={(slice) => {
            const datum = d.pamDistribution.find((entry) => entry.name === slice.label);
            if (datum) d.openDrilldown("pam", "PAM Coverage", datum);
          }}
        />
      </div>

      {/* Privileged Group By Type */}
      <div className="flex flex-col min-w-0 rounded-xl border border-border bg-card p-5">
        <h3 className="mb-4 font-semibold text-foreground">Privileged Group By Type</h3>
        {hasAccountsByGroup ? (
          <SpikeChart
            data={d.accountsByGroup}
            prefix="pag-type"
            tickVariant="badge"
            onSelect={(datum) =>
              d.openDrilldown("group", "Privileged Group By Type", datum)
            }
          />
        ) : (
          <ChartEmptyState />
        )}
        {hasAccountsByGroup && (
          <ChartSummary
            data={d.accountsByGroup}
            highestLabel="Highest Count"
            lowestLabel="Least Count"
          />
        )}
      </div>
    </div>
  );
}

interface PrivilegedAccountsTableSectionProps {
  data: ReturnType<typeof useInsightsData>;
  columns: Column<any>[];
}

function PrivilegedAccountsTableSection({
  data: d,
  columns,
}: PrivilegedAccountsTableSectionProps) {
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5">
      <h3 className="font-semibold text-foreground">Privileged Account Inventory</h3>

      <TableToolbar
        className="max-h-fit"
        search={{
          value: d.search,
          onChange: d.setSearch,
          onSubmit: d.submitSearch,
          placeholder: "Search",
          maxLength: 80,
          ariaLabel: "Search privileged accounts",
        }}
        filters={[
          {
            key: "risk",
            leadingLabel: "Risk",
            multiple: true,
            values: d.riskFilters,
            onValuesChange: (values) => {
              d.setRiskFilters(values);
              d.setPage(0);
            },
            placeholder: "All",
            options: d.riskFilterOptions,
          },
          {
            key: "groupName",
            leadingLabel: "Group",
            multiple: true,
            values: d.groupFilters,
            onValuesChange: (values) => {
              d.setGroupFilters(values);
              d.setPage(0);
            },
            placeholder: "All",
            options: d.groupFilterOptions,
          },
          {
            key: "pamStatus",
            leadingLabel: "PAM Status",
            multiple: true,
            values: d.pamFilters,
            onValuesChange: (values) => {
              d.setPamFilters(values);
              d.setPage(0);
            },
            placeholder: "All",
            options: d.pamFilterOptions.map((value) => ({ value, label: value })),
          },
        ]}
        pageSize={d.pageSize}
        pageSizeLabel="Rows per page"
        onPageSizeChange={(size) => {
          d.setPageSize(size);
          d.setPage(0);
        }}
      />

      <div
        className={`transition-opacity ${
          d.accountsLoading && d.accounts.length > 0 ? "pointer-events-none opacity-60" : ""
        }`}
      >
        <DataTable
          columns={columns}
          data={d.visibleAccounts}
          keyField="accountName"
          loading={d.accountsLoading}
          page={d.page}
          pageSize={d.pageSize}
          totalItems={d.accountsTotal}
          onPageChange={d.setPage}
          sortKey={d.sortKey}
          sortDir={d.sortDir}
          onSort={d.handleSort}
          maxHeight="550px"
          itemLabel="accounts"
          emptyMessage="No Data Available"
        />
      </div>
    </div>
  );
}

/* ── Main Insights Page Component ───────────────────────────────────── */

export const PrivilegeAccessInsights = () => {
  const d = useInsightsData();

  const columns = useMemo(
    () =>
      buildAccountColumns({
        hideSelect: true,
        hideReview: true,
        hideDetail: true,
      }),
    [],
  );

  const drilldownColumns = useMemo(
    () => buildDrilldownColumns(columns, { hideReview: true, hideDetail: true }),
    [columns],
  );

  const inventoryColumns = useMemo(
    () => buildInventoryColumns(d.breakdown?.sourceSystemName, d.openDrilldown),
    [d.breakdown?.sourceSystemName, d.openDrilldown],
  );

  const privilegedColumns = useMemo(
    () =>
      buildPrivilegedColumns(
        d.privilegedAccountBreakdown?.sourceSystemName,
        d.openDrilldown,
      ),
    [d.privilegedAccountBreakdown?.sourceSystemName, d.openDrilldown],
  );

  return (
    <div className="flex flex-col gap-5 pb-10">
      <InsightsTopFilters data={d} />

      {d.appliedSourceSystemIds.length === 0 ? (
        <InsightsEmptyPrompt />
      ) : (
        <>
          <InsightsBreakdownsSection
            data={d}
            inventoryColumns={inventoryColumns}
            privilegedColumns={privilegedColumns}
          />
          <InsightsChartsSection data={d} />
          <PrivilegedAccountsTableSection data={d} columns={columns} />
        </>
      )}

      <PagChartDrilldownDrawer
        drilldown={d.drilldown}
        sourceSystemIds={d.appliedSourceSystemIds}
        columns={drilldownColumns}
        refreshToken={d.reloadToken}
        filterOptionsData={d.filterOptionsData}
        onClose={() => d.setDrilldown(null)}
      />
    </div>
  );
};
